import React from 'react';
import { useTranslation } from 'react-i18next';
import { IVideoClip, VideoEditConfig, IVideoClipResource, IEpisode } from '@packages/global';
import { PhotoIcon, MusicalNoteIcon, VideoCameraIcon, FilmIcon } from '@heroicons/react/24/outline';
import ImageGenerationForm from './forms/ImageGenerationForm';
import AudioGenerationForm from './forms/AudioGenerationForm';
import VideoGenerationForm from './forms/VideoGenerationForm';
import StoryboardForm from './forms/StoryboardForm';
import { useModels } from '../../hooks/useModels';

type ResourceTab = 'image' | 'audio' | 'video' | 'storyboard';

interface ResourcePanelProps {
  selectedVideoClip: IVideoClip | null;
  activeTab: ResourceTab;
  onTabChange: (tab: ResourceTab) => void;
  imageFrameType?: 'start' | 'end' | 'storyboard';
  onImageFrameTypeChange?: (type: 'start' | 'end' | 'storyboard') => void;
  refreshVideoClip?: (clipId: string) => Promise<void>;
  editConfig?: VideoEditConfig | null;
  onEditConfigChange?: (config: VideoEditConfig) => void;
  // 资源轮询相关
  resourceStatusMap?: Map<string, IVideoClipResource[]>;
  pendingClipIds?: Set<string>;
  onTriggerResourcePoll?: () => Promise<void>;
  episode?: IEpisode | null;
}

export default function ResourcePanel({
  selectedVideoClip,
  activeTab,
  onTabChange,
  imageFrameType = 'start',
  onImageFrameTypeChange,
  refreshVideoClip,
  editConfig,
  onEditConfigChange,
  resourceStatusMap = new Map(),
  pendingClipIds = new Set(),
  onTriggerResourcePoll,
  episode
}: ResourcePanelProps) {
  const { models, loading: modelsLoading } = useModels();
  const { t } = useTranslation(['editor', 'common']);

  // 获取当前选中 clip 的资源列表
  const currentClipResources = selectedVideoClip
    ? resourceStatusMap.get(selectedVideoClip.id) || []
    : [];

  // 检查当前 clip 是否有进行中的任务
  const isCurrentClipPending = selectedVideoClip
    ? pendingClipIds.has(selectedVideoClip.id)
    : false;

  return (
    <div className="flex flex-col h-full bg-bg-surface border-r border-border-subtle">
      {/* Header with Tabs - 高度与左侧 header 一致 (h-14 = 56px) */}
      <div className="h-14 flex-shrink-0 border-b border-border-subtle bg-bg-surface">
        <div className="flex h-full">
          <button
            onClick={() => onTabChange('storyboard')}
            className={`flex-1 flex items-center justify-center gap-1 px-3 text-sm font-medium transition-colors ${activeTab === 'storyboard'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-400 hover:text-gray-300'
              }`}
          >
            <FilmIcon className="w-4 h-4" />
            <span className={`text-xs`}>
              {t('editor:resourcePanel.storyboard')}
            </span>
          </button>
          <button
            onClick={() => onTabChange('image')}
            className={`flex-1 flex items-center justify-center gap-1 px-3 text-sm font-medium transition-colors ${activeTab === 'image'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-400 hover:text-gray-300'
              }`}
          >
            <PhotoIcon className="w-4 h-4" />
            <span className={`text-xs`}>
              {t('editor:resourcePanel.image')}
            </span>
          </button>
          <button
            onClick={() => onTabChange('audio')}
            className={`flex-1 flex items-center justify-center gap-1 px-34 text-sm font-medium transition-colors ${activeTab === 'audio'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-400 hover:text-gray-300'
              }`}
          >
            <MusicalNoteIcon className="w-4 h-4" />
            <span className={`text-xs`}>
              {t('editor:resourcePanel.dubbing')}
            </span>
          </button>
          <button
            onClick={() => onTabChange('video')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 text-sm font-medium transition-colors ${activeTab === 'video'
              ? 'text-primary border-b-2 border-primary'
              : 'text-gray-400 hover:text-gray-300'
              }`}
          >
            <VideoCameraIcon className="w-5 h-5" />
            <span className={`text-xs`}>
              {t('editor:resourcePanel.video')}
            </span>
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto">
        {!selectedVideoClip ? (
          <div className="flex items-center justify-center h-full p-6">
            <div className="text-center">
              <PhotoIcon className="w-12 h-12 mx-auto mb-3 text-gray-600" />
              <p className="text-gray-400 text-sm">{t('editor:selectClipFirst')}</p>
            </div>
          </div>
        ) : (
          <div className="h-full">
            {activeTab === 'storyboard' ? (
              <StoryboardForm
                videoClip={selectedVideoClip}
                refreshVideoClip={refreshVideoClip}
              />
            ) : activeTab === 'image' ? (
              <ImageGenerationForm
                videoClip={selectedVideoClip}
                models={models}
                modelsLoading={modelsLoading}
                frameType={imageFrameType}
                onFrameTypeChange={onImageFrameTypeChange}
                refreshVideoClip={refreshVideoClip}
                editConfig={editConfig}
                resources={currentClipResources}
                isPolling={isCurrentClipPending}
                onTriggerPoll={onTriggerResourcePoll}
                episode={episode}
              />
            ) : activeTab === 'audio' ? (
              <AudioGenerationForm
                videoClip={selectedVideoClip}
                models={models}
                modelsLoading={modelsLoading}
                refreshVideoClip={refreshVideoClip}
                resources={currentClipResources}
                isPolling={isCurrentClipPending}
                onTriggerPoll={onTriggerResourcePoll}
              />
            ) : (
              <VideoGenerationForm
                videoClip={selectedVideoClip}
                models={models}
                modelsLoading={modelsLoading}
                refreshVideoClip={refreshVideoClip}
                editConfig={editConfig}
                onEditConfigChange={onEditConfigChange}
                resources={currentClipResources}
                isPolling={isCurrentClipPending}
                onTriggerPoll={onTriggerResourcePoll}
                episode={episode}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
